import React, { useMemo, useRef } from 'react';
import cn from 'classnames';
import PageSeo from '@framework/seo/PageSeo';
import { useRouter } from 'next/router';
import StaticTemplateTabs from './StaticTemplateTabs';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import { useSetPageAsStatic } from './StaticPagesContext';
import useElementSize from '@framework/hooks/useElementSize';
import { HEADER_WRAPPER_ID } from '@/creditflowresearch/ui/common/components/header/constants';

import styles from './staticTemplate.module.scss';

interface IProps {
  className: string,
  pages: {
    name: string,
    id: string,
    path: string,
    component: React.FC,
    metaTitle?: string,
    metaDescription?: string,
    hidden?: boolean,
    withoutSchemaMerging?: boolean,
    metaSchema?: Record<string, string | number | Object>,
  }[],
}

const StaticTemplate = (props: IProps) => {
  useSetPageAsStatic();
  const { pathname } = useRouter();
  const { height: topOffset } = useElementSize(HEADER_WRAPPER_ID);
  const containerRef = useRef(null);
  const isSinglePage = props.pages.length === 1;
  const activePage = isSinglePage ? props.pages[0] : props.pages.find((page) => pathname.startsWith(page.path));
  const pageTabs = useMemo(() => (props.pages.map((page) => ({
    id: page.id,
    path: page.path,
    name: page.name,
  }))), [props.pages]);

  if (!activePage) {
    return null;
  }

  const metaData = {
    title: activePage.metaTitle,
    url: activePage.path,
    description: activePage.metaDescription,
  };

  return (
    <>
      <PageSeo { ...metaData } />
      <MetaSchema schema={ activePage.metaSchema } isWithoutMerging={ activePage.withoutSchemaMerging } />
      <div
        className={ styles.container }
        ref={ containerRef }
        style={ {
          minHeight: `calc(100vh - ${ topOffset }px)`,
        } }
      >
        { !isSinglePage && (
          <StaticTemplateTabs
            tabs={ pageTabs }
          />
        ) }
        <div className={ cn(styles.content, props.className) }>
          { React.createElement(activePage.component) }
        </div>
      </div>
    </>
  );
};

export default StaticTemplate;
